// "Back where you were, with a welcome."
//
// After a sign-in the guest lands on the page they came from (the form's
// redirect), and this says so once: "Welcome back, Sara", or "You're signed
// in" for a new account or one with no name yet.
//
// It keys off a note the sign-in leaves in sessionStorage (see
// post-sign-in.ts), not off the session itself, so an ordinary page load of a
// signed-in guest never shows it. The note survives the Google and Apple
// round trip, which reloads the whole page.
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { PW, pewterPanelApproved } from "@majourneys/booking";
import { Icon } from "@majourneys/icons";
import { useSession } from "../../../lib/auth";
import { WELCOME_EVENT, greetingName, isNewAccount, takeWelcome } from "../lib/post-sign-in";

const SHOW_MS = 5000;

type SessionUser = { name?: string | null; email?: string | null; createdAt?: string | Date | null };

export function SignInWelcome() {
  const { t } = useTranslation();
  const { data: session } = useSession() as { data?: { user?: SessionUser } | null };
  const user = session?.user ?? null;
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    const check = () => {
      if (!takeWelcome()) return;
      const name = isNewAccount(user) ? null : greetingName(user);
      setMessage(name ? t("auth.one_field.welcome_back", { name }) : t("auth.one_field.signed_in"));
    };
    check();
    window.addEventListener(WELCOME_EVENT, check);
    return () => window.removeEventListener(WELCOME_EVENT, check);
  }, [user, t]);

  useEffect(() => {
    if (!message) return;
    const id = window.setTimeout(() => setMessage(null), SHOW_MS);
    return () => window.clearTimeout(id);
  }, [message]);

  if (!message) return null;
  const panel = pewterPanelApproved(999);
  return (
    <div className="pointer-events-none fixed inset-x-0 top-4 z-[var(--z-toast,60)] flex justify-center px-4">
      <div
        role="status"
        className="pointer-events-auto flex max-w-[420px] items-center gap-3 py-2 pe-2 ps-2.5"
        style={{ ...panel, boxShadow: `${panel.boxShadow}, 0 18px 40px -20px rgba(17,14,57,0.45)` }}
      >
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full" style={{ background: PW.greenTint }}>
          <Icon name="ri-check-line" className="text-[17px]" style={{ color: PW.green }} aria-hidden="true" />
        </span>
        <span className="min-w-0 break-words text-[14.5px] font-semibold" style={{ color: PW.navy }}>
          {message}
        </span>
        <button
          type="button"
          onClick={() => setMessage(null)}
          aria-label={t("common.close")}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition-colors hover:bg-[rgba(17,14,57,0.06)]"
          style={{ color: PW.muted }}
        >
          <Icon name="ri-close-line" className="text-[17px]" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
